import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AITier = 'FREE' | 'PAID';
export type TimeInputStyle = 'SMART_WHEEL' | 'CLASSIC_LIST';
export type EngineMode = 'HEURISTIC' | 'PHARMACOKINETIC';

interface ConfigState {
    activeTier: AITier;
    timeInputStyle: TimeInputStyle;
    engineMode: EngineMode;
    developerMode: boolean;
    // Tri-Core Telemetry Toggles
    showDailyLedger: boolean;
    showLiveFuelGauge: boolean;
    showGlycogenSink: boolean;
    enableCarryoverInLedger: boolean;
}

interface ConfigActions {
    setActiveTier: (tier: AITier) => void;
    setTimeInputStyle: (style: TimeInputStyle) => void;
    setEngineMode: (mode: EngineMode) => void;
    setDeveloperMode: (active: boolean) => void;
    toggleTelemetry: (key: keyof Pick<ConfigState, 'showDailyLedger' | 'showLiveFuelGauge' | 'showGlycogenSink' | 'enableCarryoverInLedger'>) => void;
    getActiveKey: () => string;
}

/**
 * useConfigStore (v4)
 * Central authority for application settings and API tier management.
 * HARDENED: Environment accessor for TypeScript stability.
 */
export const useConfigStore = create<ConfigState & ConfigActions>()(
    persist(
        (set, get) => ({
            activeTier: 'FREE',
            timeInputStyle: 'SMART_WHEEL',
            engineMode: 'HEURISTIC',
            developerMode: false,
            
            showDailyLedger: true,
            showLiveFuelGauge: true,
            showGlycogenSink: true,
            enableCarryoverInLedger: false,

            setActiveTier: (tier) => set({ activeTier: tier }),
            setTimeInputStyle: (style) => set({ timeInputStyle: style }),
            setEngineMode: (mode) => set({ engineMode: mode }),
            setDeveloperMode: (active) => set({ developerMode: active }),
            toggleTelemetry: (key) => set((state) => ({ [key]: !state[key] })),

            getActiveKey: () => {
                const tier = get().activeTier;
                const env = (import.meta as any).env;
                
                return tier === 'PAID' 
                    ? env.VITE_PAID_API 
                    : env.VITE_FREE_API;
            }
        }),
        { name: 'mp-config-settings' }
    )
);
